import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import "./styles/global.css";
import App, { preloadAnonymousPublicRoute } from './App';
import { HelmetProvider } from 'react-helmet-async';
import {
  getStoredAuthTokens,
  getUser,
  initializeAuthStorage,
} from "./utils/auth";
import { scheduleAfterLcpIdleOrInteraction } from "./utils/deferPublicMonitoring";
import { isAnonymousSessionOptionalRoute } from "./utils/publicRouteMode";

initializeAuthStorage();

const storedUser = getUser();
const { accessToken, refreshToken } = getStoredAuthTokens();
const initialPathname = typeof window !== "undefined" ? window.location.pathname || "/" : "/";
const hasAuthHint = Boolean(storedUser || accessToken || refreshToken);
const isAnonymousPublicInitialPath =
  !hasAuthHint && isAnonymousSessionOptionalRoute(initialPathname);

if (hasAuthHint || !isAnonymousPublicInitialPath) {
  import("./utils/sentry")
    .then(({ scheduleSentryBootstrap }) => {
      scheduleSentryBootstrap({
        pathname: initialPathname,
        hasAuthHint,
      });
    })
    .catch(() => {});
}

const app = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

const container = document.getElementById('root');
const hasPrerenderedMarkup = Boolean(container?.hasChildNodes());
const hasMaterializedListingShell = Boolean(
  container?.querySelector(".seo-public-listing-page")
);
const hasPublicProfileSnapshot = Boolean(
  container?.querySelector(".profile-page-musa--public-snapshot")
);

const hydrateApp = () => {
  hydrateRoot(container, app);
};

const createAppRoot = () => {
  createRoot(container).render(app);
};

if (hasMaterializedListingShell || hasPublicProfileSnapshot) {
  let didMount = false;
  const mountOnce = () => {
    if (didMount) return;
    didMount = true;
    createAppRoot();
  };
  const fallbackMountId = window.setTimeout(mountOnce, 5000);
  preloadAnonymousPublicRoute(initialPathname).finally(() => {
    window.clearTimeout(fallbackMountId);
    mountOnce();
  });
} else if (hasPrerenderedMarkup) {
  hydrateApp();
} else if (container) {
  createAppRoot();
}

const scheduleWebVitals = () => {
  const loadWebVitals = () => {
    import("./utils/webVitals")
      .then(({ initWebVitals }) => {
        initWebVitals();
      })
      .catch(() => {});
  };

  if (isAnonymousPublicInitialPath && typeof window !== "undefined") {
    scheduleAfterLcpIdleOrInteraction(loadWebVitals, {
      timeoutMs: hasPrerenderedMarkup ? 9000 : 7000,
      idleTimeoutMs: 2000,
      lcpSettleMs: 4500,
      deferInteractionUntilIdle: true,
      interactionSettleMs: 180,
    });
    return;
  }

  loadWebVitals();
};

scheduleWebVitals();
